import { useState, useEffect } from "react";
import { SiteFooter } from "../components/SiteFooter";
import { toast } from "sonner";
import { ShoppingBag, Plus, Minus, Trash2, ArrowRight, CheckCircle, ChevronDown, Shield, Search, Package } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_da98dbcd-f751-4482-bd4e-a7e20426ba71/artifacts/tm6wbpvj_CD8D187A-3FEA-430F-A897-685C7F8A2178.png";
const CASHAPP_URL = "https://cash.app/$KookaMF1";
const CASHAPP_QR = "https://static.prod-images.emergentagent.com/jobs/da98dbcd-f751-4482-bd4e-a7e20426ba71/images/ddbaa0d5b27d1f365278e798a2d3bc00d34371e494904ceef2efa33e5a6b8a6d.jpeg";

/* Strain/flavor options */
const WAFFLE_CONE_FLAVORS = ["Oreo Milkshake", "Blueberry Yogurt", "Salted Caramel", "Strawberry n Cream"];

/* THC Edible Brands — organized by type */
const THC_CHOCOLATE_BRANDS = [
  "Almond Joint", "Reefers", "Mr. Goodbud", "THC Snickers", "THC Twix",
  "Crunch THC", "Hershey THC", "Milk Weed", "Payweed", "Almond Weed",
  "Marijuanos", "Dank Nuggs (Jar)",
];
const THC_CANDY_BRANDS = [
  /* Trolli */
  "Trolli Cherry Bombers (500mg)", "Trolli Big Bold Bears", "Trolli Apple O's",
  "Trolli Squiggles", "Trolli Gummy Variety",
  /* HYFLY */
  "HYFLY Blue Raspberry Bears (600mg)", "HYFLY Tangerine Bears (600mg)",
  "HYFLY Sour Watermelon Rings (600mg)", "HYFLY Sour Bears Neon (600mg)",
  "HYFLY Sour Blue Raspberry Rings", "HYFLY Worms Mix",
  /* Medicated */
  "Medicated Nerds Rope Bites (600mg)", "Med Head Blueberry (500mg)",
  "Medibles Doritos-Style", "Medibles Cheetos-Style",
  /* Candy Fruits */
  "Candy Fruits Strawnana (1000mg)", "Candy Fruits Variety",
  /* Classic Style */
  "Dank Gummies (500mg)", "Weedish Fish", "Baribo Happy Cherries",
  "Chuckles Gummy Worms (400mg)", "Neon Worms",
  "Warheads Sour Chewy Cubes (500mg)", "Sour Patch Medicated Gummies",
  "Sour Strawberry Watermelon Bites",
  /* Other */
  "Cap'n Crunch Berries Edible", "One Up Gummies",
  "LOL Edibles Candy (500mg)", "Mango Kush Gummies",
  "Cannabis Crunchy Peanut Butter Candy",
  /* New brands */
  "Bored Hash Gummies", "Nugz Bars", "Clarity Gummies", "Cereal Bars",
  "Squish Mini", "MedLab Bars", "Galaxy Sour Belts", "California Gold Gummies",
  "Devour Gummies/Ropes", "Fade Gummies", "Golden Ticket Gummies",
  "Pounds Gummies", "Squish Gummies", "Pounds Nuggz",
  "Devour Syrups", "Frootz Gummies", "Pounds Syrup",
];
const MOONROCK_FLAVORS = {
  "Slurpee Edition": ["Blue Slurpee", "Purple Rain", "Watermelon Ice", "Strawberry Slush", "Tropical Punch"],
  "Cereal Edition": ["Fruity Pebbles", "Cap'N Crunch", "Cinnamon Toast", "Cocoa Krispies", "Honey Berry"],
  "Exotic Edition": ["Zkittlez", "Gelato", "Runtz", "Biscotti", "Sherbert"],
  "Cake Edition": ["Wedding Cake", "Birthday Cake", "Pink Cake", "Lemon Cake", "Red Velvet"],
  "Snowcap Edition": ["Snowcap", "Kush Mints", "Ice Cream Cake", "Frozen Zkittlez", "White Truffle"],
};

const MOONROCK_EDITIONS_LIST = Object.keys(MOONROCK_FLAVORS);

const CONCENTRATE_STRAINS = [
  "Splitz", "Snooze Drool", "50/50 Whole M", "Cake Badder", "Havana Sugar",
  "Vice City Sugar", "Exotic WM Sugar", "Winter WM Sugar", "Slurpee WM Sugar",
  "Sprinkles WM Sugar", "Gelato WM Sugar", "Candy WM Sugar", "Donuts",
  "Whole Melt Extracts", "Whole Melt Live Resin",
];

const MUSHROOM_STRAINS = ["Penis Envy", "PE x Blue Meanie", "Iceberg", "Makilla Gorilla", "A.P.E.", "Blue Vuitton", "Golden Teacher"];

const PRODUCTS = [
  { category: "Vapes & Carts", items: [
    { name: "2G Disposable", price: 50 },
    { name: "3G Disposable", price: 65 },
    { name: "3.5G Disposable", price: 70 },
    { name: "1G Screw-On Cart", price: 30 },
    { name: "Wholesale Box (100 pcs)", price: 1400 },
  ]},
  { category: "KAWS Rocks", items: [
    { name: "Moonrock Oz", price: 250, hasEdition: true },
    { name: "Moonrock Full Box (16 Jars)", price: 1400, hasEditionOnly: true },
  ]},
  { category: "Standard Concentrates", items: [
    { name: "Standard 1G", price: 30, hasStrain: true },
    { name: "Standard 3G", price: 75, hasStrain: true },
    { name: "Standard 5G", price: 100, hasStrain: true },
    { name: "Standard 7G", price: 105, hasStrain: true },
    { name: "Standard 14G (\u00bd OZ)", price: 210, hasStrain: true },
    { name: "Standard 1 OZ", price: 420, hasStrain: true },
  ]},
  { category: "Premium Concentrates", items: [
    { name: "Diamond Sugar Wax 1G", price: 55, hasStrain: true },
    { name: "Diamond Sugar Wax 3G", price: 155, hasStrain: true },
    { name: "Diamond Sugar Wax 5G", price: 245, hasStrain: true },
    { name: "Diamond Sugar Wax 7G", price: 350, hasStrain: true },
    { name: "Diamond Sugar Wax 14G", price: 700, hasStrain: true },
    { name: "Diamond Sugar Wax 1 OZ", price: 1400, hasStrain: true },
    { name: "Solid Diamond 1G", price: 60, fixedStrain: "Sweet Fire Funk — 710Labs" },
    { name: "Solid Diamond 3G", price: 170, fixedStrain: "Sweet Fire Funk — 710Labs" },
    { name: "Solid Diamond 5G", price: 270, fixedStrain: "Sweet Fire Funk — 710Labs" },
    { name: "Solid Diamond 7G", price: 350, fixedStrain: "Sweet Fire Funk — 710Labs" },
    { name: "Solid Diamond 14G", price: 700, fixedStrain: "Sweet Fire Funk — 710Labs" },
    { name: "Solid Diamond 1 OZ", price: 1400, fixedStrain: "Sweet Fire Funk — 710Labs" },
    { name: "Live Rosin (Dank Rosin) 1G", price: 60 },
    { name: "Live Rosin (Dank Rosin) 3G", price: 170 },
    { name: "Live Rosin (Dank Rosin) 5G", price: 270 },
    { name: "Live Rosin (Dank Rosin) 7G", price: 350 },
    { name: "Live Rosin (Dank Rosin) 14G", price: 700 },
    { name: "Live Rosin (Dank Rosin) 1 OZ", price: 1400 },
  ]},
  { category: "Mushrooms", items: [
    { name: "Waffle Cones (4G)", price: 50, hasWaffleFlavor: true },
    { name: "Mushroom Chocolate Bar", price: 50 },
    { name: "Mushroom Gummies", price: 40 },
    { name: "Vision Mushroom Gummies", price: 60 },
    { name: "Raw Mushroom Oz", price: 130, hasMushStrain: true },
    { name: "Raw Mushroom Half P", price: 600, hasMushStrain: true },
    { name: "Raw Mushroom Full Unit", price: 1000, hasMushStrain: true },
  ]},
  { category: "Featured Concentrates", items: [
    { name: "Dirty Sprite (7G Resin)", price: 105 },
    { name: "Pure Ice Cream (7G Badder)", price: 105 },
    { name: "Hash — Hubba Bubba", price: 55 },
  ]},
  { category: "Hash / Rosin", items: [
    { name: "Hash 1000G", price: 1200 },
    { name: "Hash 500G", price: 600 },
    { name: "LA Mousse (100G Bar)", price: 600 },
    { name: "Bubble Hash (100G Bar)", price: 600 },
    { name: "Static Hash (50G Bar)", price: 600 },
  ]},
  { category: "Flower & Edibles", items: [
    { name: "Breakdown Pack Oz", price: 100 },
    { name: "Candy Edible", price: 30, hasCandyBrand: true },
    { name: "Chocolate Edible", price: 30, hasChocBrand: true },
    { name: "Gummy Edible", price: 40, hasCandyBrand: true },
  ]},
  { category: "Oz Specials", items: [
    { name: "Zour Smallz (Oz)", price: 100 },
    { name: "Frog Venom (Oz)", price: 100 },
  ]},
];

const BUSINESS_POLICY = [
  { title: "No Credit Policy", desc: "All orders must be paid in full before processing. We accept multiple forms of payment for your convenience." },
  { title: "Out of Stock?", desc: "If an item is temporarily out of stock, we will restock promptly and ship your order the following day. In-person pickup is also available." },
  { title: "Communication Matters", desc: "Orders may be cancelled or redirected if there is a lack of communication or unnecessary delays. Please remain responsive and respectful." },
  { title: "Check Your Funds", desc: "Before placing an order, please ensure you have sufficient funds to cover the full amount. Adjust your order accordingly if needed." },
  { title: "Trusted Source", desc: "Once payment is received, we are committed to fulfilling and delivering your order. Quality packaging and extra protection at no additional cost." },
  { title: "Wholesale Advantage", desc: "We operate on low margins to provide you with strong returns and long-term business opportunities." },
];

/* ========== Component ========== */

export default function OrderPage() {
  const [cart, setCart] = useState([]);
  const [specialRequests, setSpecialRequests] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [shipping, setShipping] = useState({ line1: "", line2: "", city: "", state: "", zip: "" });
  const [submitting, setSubmitting] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [activeCategory, setActiveCategory] = useState(PRODUCTS[0].category);
  const [showPolicy, setShowPolicy] = useState(false);
  const [trackId, setTrackId] = useState("");
  const [trackResult, setTrackResult] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [soldOut, setSoldOut] = useState(new Set());
  /* Strain/flavor selections per product */
  const [selections, setSelections] = useState({});

  useEffect(() => {
    axios.get(`${API}/sold-out`).then((res) => setSoldOut(new Set(res.data))).catch(() => {});
  }, []);

  const total = cart.reduce((sum, item) => sum + item.price * item.qty, 0);

  const trackOrder = async () => {
    if (!trackId.trim()) return;
    setTracking(true);
    setTrackResult(null);
    try {
      const res = await axios.get(`${API}/orders/track/${trackId.trim()}`);
      setTrackResult(res.data);
    } catch {
      setTrackResult({ error: true });
    } finally { setTracking(false); }
  };

  const addItem = (product) => {
    const sel = product.fixedStrain || selections[product.name] || "";
    const label = sel ? `${product.name} — ${sel}` : product.name;
    setCart((prev) => {
      const existing = prev.find((c) => c.label === label);
      if (existing) return prev.map((c) => c.label === label ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { label, name: product.name, strain: sel || "", price: product.price, qty: 1 }];
    });
  };

  const updateQty = (label, delta) => {
    setCart((prev) => prev.map((c) => c.label === label ? { ...c, qty: Math.max(1, c.qty + delta) } : c));
  };

  const removeItem = (label) => setCart((prev) => prev.filter((c) => c.label !== label));

  const canSubmit = cart.length > 0 && contact.name.trim() && contact.email.trim() && contact.phone.trim() && shipping.line1.trim() && shipping.city.trim() && shipping.state.trim() && shipping.zip.trim();

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        items: cart.map((c) => ({ product: c.label, quantity: c.qty, unit_price: c.price, subtotal: c.price * c.qty })),
        special_requests: specialRequests,
        name: contact.name, email: contact.email, phone: contact.phone,
        address_line1: shipping.line1, address_line2: shipping.line2,
        city: shipping.city, state: shipping.state, zip_code: shipping.zip,
      };
      const res = await axios.post(`${API}/orders`, payload);
      setOrderId(res.data.id);
      toast.success("Order placed!");
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  if (orderId) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-primary)" }} data-testid="order-confirmed">
        <div className="px-5 sm:px-8 md:px-12 py-8 border-b" style={{ borderColor: "var(--border)" }}>
          <a href="/"><img src={LOGO_URL} alt="Green Routes" className="h-9 sm:h-10 mx-auto logo-blend" /></a>
        </div>
        <div className="flex-1 flex items-center justify-center px-5 py-12">
          <div className="max-w-md w-full text-center">
            <CheckCircle size={48} className="mx-auto mb-4" style={{ color: "var(--success)" }} />
            <h1 className="text-2xl sm:text-3xl tracking-tight mb-2" style={{ color: "var(--text-primary)", fontFamily: "'Playfair Display', serif" }}>Order #{orderId}</h1>
            <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>Your order has been placed. Complete payment below to confirm shipping.</p>
            <div className="p-5 mb-6" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }}>
              <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "var(--text-tertiary)" }}>Order Total</p>
              <p className="text-3xl font-bold" style={{ color: "var(--primary)", fontFamily: "'JetBrains Mono', monospace" }}>${total.toFixed(2)}</p>
            </div>
            <a href={CASHAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="cashapp-pay-btn"
              className="inline-flex items-center justify-center gap-2 w-full py-4 text-sm font-semibold tracking-wide mb-4"
              style={{ background: "#00D632", color: "#fff" }}>
              Pay with Cash App — $KookaMF1
            </a>
            <p className="text-xs mb-6" style={{ color: "var(--text-tertiary)" }}>Include order # <strong>{orderId}</strong> in the CashApp note</p>
            <div className="p-5 mb-4 flex flex-col items-center" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }}>
              <p className="text-xs uppercase tracking-wider font-bold mb-4 text-center" style={{ color: "var(--text-tertiary)" }}>Or scan to pay</p>
              <img src={CASHAPP_QR} alt="Cash App QR Code — $KookaMF1" style={{ width: "240px", height: "240px", objectFit: "contain", borderRadius: "12px" }} data-testid="cashapp-qr" />
              <p className="text-xs mt-3" style={{ color: "var(--text-tertiary)" }}>$KookaMF1</p>
            </div>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-primary)" }} data-testid="order-page">
      <div className="px-5 sm:px-8 md:px-12 py-8 border-b" style={{ borderColor: "var(--border)" }}>
        <a href="/"><img src={LOGO_URL} alt="Green Routes" className="h-9 sm:h-10 mx-auto logo-blend" /></a>
      </div>

      <div className="flex-1 px-5 sm:px-8 md:px-12 lg:px-20 py-8 md:py-12">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase mb-2 font-semibold" style={{ color: "var(--text-tertiary)" }}>KookaMF Retail</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight mb-6" style={{ color: "var(--text-primary)", fontFamily: "'Playfair Display', serif" }}>Place Your Order</h1>

          {/* Business Policy */}
          <div className="mb-8" style={{ border: "1px solid var(--border)" }} data-testid="business-policy">
            <button onClick={() => setShowPolicy(!showPolicy)} className="w-full flex items-center justify-between px-5 py-3.5 text-left"
              style={{ background: "var(--bg-secondary)" }} data-testid="business-policy-toggle">
              <div className="flex items-center gap-2.5">
                <Shield size={15} style={{ color: "var(--primary)" }} />
                <span className="text-xs uppercase tracking-[0.15em] font-bold" style={{ color: "var(--text-primary)" }}>Business Policy — Please Read Before Ordering</span>
              </div>
              <ChevronDown size={14} style={{ color: "var(--text-tertiary)", transform: showPolicy ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }} />
            </button>
            {showPolicy && (
              <div className="px-5 py-4 space-y-4" style={{ borderTop: "1px solid var(--border)" }}>
                {BUSINESS_POLICY.map((p, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-xs font-bold" style={{ background: "var(--primary)", color: "#F5F3E9", borderRadius: "50%" }}>{i + 1}</span>
                    <div>
                      <p className="text-sm font-semibold mb-0.5" style={{ color: "var(--text-primary)" }}>{p.title}</p>
                      <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>{p.desc}</p>
                    </div>
                  </div>
                ))}
                <div className="flex flex-wrap gap-4 pt-3 mt-2" style={{ borderTop: "1px solid var(--border)" }}>
                  {["Fast Service", "Quality Products", "Discreet Shipping", "Good Energy Only"].map((tag) => (
                    <span key={tag} className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "var(--text-tertiary)" }}>{tag}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Order Tracker */}
          <div className="mb-8" style={{ border: "1px solid var(--border)" }} data-testid="order-tracker">
            <div className="px-5 py-4" style={{ background: "var(--bg-secondary)" }}>
              <div className="flex items-center gap-2.5 mb-3">
                <Package size={15} style={{ color: "var(--primary)" }} />
                <span className="text-xs uppercase tracking-[0.15em] font-bold" style={{ color: "var(--text-primary)" }}>Track Your Order</span>
              </div>
              <div className="flex gap-2">
                <input type="text" value={trackId} onChange={(e) => setTrackId(e.target.value)} placeholder="Enter order #"
                  onKeyDown={(e) => e.key === "Enter" && trackOrder()}
                  className="flex-1 px-3 py-2 text-sm outline-none" data-testid="track-order-input"
                  style={{ background: "var(--bg-elevated, #fff)", border: "1px solid var(--border)", color: "var(--text-primary)", fontFamily: "'JetBrains Mono', monospace" }} />
                <button onClick={trackOrder} disabled={tracking} data-testid="track-order-btn"
                  className="px-4 py-2 text-xs font-semibold flex items-center gap-1.5"
                  style={{ background: "var(--primary)", color: "#F5F3E9" }}>
                  <Search size={12} /> {tracking ? "..." : "Track"}
                </button>
              </div>
              {trackResult && !trackResult.error && (
                <div className="mt-4 p-4" style={{ background: "var(--bg-elevated, #fff)", border: "1px solid var(--border)" }} data-testid="track-result">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-bold" style={{ color: "var(--text-primary)", fontFamily: "'JetBrains Mono', monospace" }}>#{trackResult.id}</p>
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1"
                      style={{ background: trackResult.status === "delivered" ? "rgba(16,185,129,0.1)" : trackResult.status === "shipped" ? "rgba(59,130,246,0.1)" : trackResult.status === "paid" ? "rgba(168,85,247,0.1)" : trackResult.status === "cancelled" ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.1)",
                        color: trackResult.status === "delivered" ? "#10B981" : trackResult.status === "shipped" ? "#3B82F6" : trackResult.status === "paid" ? "#a855f7" : trackResult.status === "cancelled" ? "#EF4444" : "#F59E0B" }}
                      data-testid="track-status">{trackResult.status?.replace(/_/g, " ")}</span>
                  </div>
                  <div className="space-y-1 mb-3">
                    {trackResult.items?.map((item, i) => (
                      <div key={i} className="flex justify-between text-xs" style={{ color: "var(--text-secondary)" }}>
                        <span>{item.product} &times;{item.quantity}</span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>${item.subtotal?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between pt-2" style={{ borderTop: "1px solid var(--border)" }}>
                    <span className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>Total</span>
                    <span className="text-sm font-bold" style={{ color: "var(--primary)", fontFamily: "'JetBrains Mono', monospace" }}>${trackResult.total?.toFixed(2)}</span>
                  </div>
                  {trackResult.created_at && <p className="text-[10px] mt-2" style={{ color: "var(--text-tertiary)" }}>Placed {new Date(trackResult.created_at).toLocaleDateString()}</p>}
                </div>
              )}
              {trackResult?.error && (
                <p className="mt-3 text-xs" style={{ color: "var(--error, #EF4444)" }} data-testid="track-not-found">Order not found. Check your order number and try again.</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Product Selector */}
            <div className="lg:col-span-3">
              <div className="flex gap-1 overflow-x-auto pb-3 mb-4">
                {PRODUCTS.map((cat) => (
                  <button key={cat.category} onClick={() => setActiveCategory(cat.category)} data-testid={`order-cat-${cat.category.replace(/\s/g, '-').toLowerCase()}`}
                    className="px-3 py-1.5 text-xs uppercase tracking-wider font-semibold whitespace-nowrap transition-all"
                    style={{ background: activeCategory === cat.category ? "var(--primary)" : "var(--bg-secondary)", color: activeCategory === cat.category ? "#F5F3E9" : "var(--text-tertiary)", border: "1px solid var(--border)" }}>
                    {cat.category}
                  </button>
                ))}
              </div>

              <div className="space-y-1.5" data-testid="product-list">
                {PRODUCTS.find((c) => c.category === activeCategory)?.items.map((product) => {
                  const inCart = cart.filter((c) => c.name === product.name);
                  const totalInCart = inCart.reduce((s, c) => s + c.qty, 0);
                  const currentSel = selections[product.name] || "";
                  const strainOptions = product.hasStrain ? CONCENTRATE_STRAINS : product.hasMushStrain ? MUSHROOM_STRAINS : null;
                  const editionWithFlavors = product.hasEdition;
                  const editionOnly = product.hasEditionOnly;
                  const waffleFlavors = product.hasWaffleFlavor;
                  const chocBrands = product.hasChocBrand;
                  const candyBrands = product.hasCandyBrand;
                  const hasSelector = strainOptions || editionWithFlavors || editionOnly || waffleFlavors || chocBrands || candyBrands;
                  const isSoldOut = soldOut.has(product.name);

                  return (
                    <div key={product.name} className="p-3" style={{ background: "var(--bg-elevated, #fff)", border: "1px solid var(--border)", opacity: isSoldOut ? 0.5 : 1 }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium" style={{ color: "var(--text-primary)", textDecoration: isSoldOut ? "line-through" : "none" }}>{product.name}</p>
                            {isSoldOut && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5" style={{ background: "rgba(239,68,68,0.1)", color: "#EF4444" }}>Sold Out</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold" style={{ color: "var(--primary)", fontFamily: "'JetBrains Mono', monospace" }}>${product.price}</p>
                            {product.fixedStrain && <span className="text-xs" style={{ color: "var(--text-tertiary)" }}>{product.fixedStrain}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!isSoldOut && totalInCart > 0 && <span className="text-xs px-2 py-0.5 font-semibold" style={{ background: "rgba(15,50,33,0.08)", color: "var(--primary)" }}>{totalInCart} in cart</span>}
                          {isSoldOut ? (
                            <span className="px-3 py-1.5 text-xs font-semibold" style={{ color: "var(--text-tertiary)" }}>Unavailable</span>
                          ) : (
                            <button onClick={() => addItem(product)} data-testid={`add-${product.name}`}
                              className="px-3 py-1.5 text-xs font-semibold flex items-center gap-1"
                              style={{ background: "var(--primary)", color: "#F5F3E9" }}>
                              <Plus size={12} /> Add
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Strain/Edition/Flavor selector */}
                      {hasSelector && (
                        <div className="mt-2 relative">
                          <select value={currentSel} data-testid={`strain-${product.name}`}
                            onChange={(e) => setSelections((prev) => ({ ...prev, [product.name]: e.target.value }))}
                            className="w-full text-xs py-2 px-3 outline-none appearance-none"
                            style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", color: currentSel ? "var(--text-primary)" : "var(--text-tertiary)" }}>
                            <option value="">
                              {editionOnly ? "Select edition (optional)" : editionWithFlavors ? "Select edition & flavor (optional)" : waffleFlavors ? "Select flavor (optional)" : chocBrands ? "Select chocolate brand (optional)" : candyBrands ? "Select brand (optional)" : "Select strain (optional)"}
                            </option>
                            {editionOnly && MOONROCK_EDITIONS_LIST.map((ed) => (
                              <option key={ed} value={ed}>{ed}</option>
                            ))}
                            {editionWithFlavors && MOONROCK_EDITIONS_LIST.map((ed) => (
                              <optgroup key={ed} label={ed}>
                                {MOONROCK_FLAVORS[ed].map((f) => <option key={f} value={`${ed} — ${f}`}>{f}</option>)}
                              </optgroup>
                            ))}
                            {strainOptions && strainOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                            {waffleFlavors && WAFFLE_CONE_FLAVORS.map((f) => <option key={f} value={f}>{f}</option>)}
                            {chocBrands && THC_CHOCOLATE_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                            {candyBrands && THC_CANDY_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                          </select>
                          <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--text-tertiary)" }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-6">
                <label className="block text-xs uppercase tracking-[0.15em] mb-2 font-semibold" style={{ color: "var(--text-tertiary)" }}>Special Requests (optional)</label>
                <textarea value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} rows={3} data-testid="special-requests"
                  className="w-full p-3 text-sm outline-none" placeholder="Specific strains, flavors, or notes..."
                  style={{ background: "var(--bg-elevated, #fff)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
              </div>
            </div>

            {/* Cart + Checkout */}
            <div className="lg:col-span-2">
              <div className="sticky top-8" style={{ background: "var(--bg-elevated, #fff)", border: "1px solid var(--border)" }}>
                <div className="p-5" style={{ borderBottom: "1px solid var(--border)" }}>
                  <div className="flex items-center gap-2 mb-4">
                    <ShoppingBag size={16} style={{ color: "var(--primary)" }} />
                    <p className="text-xs uppercase tracking-wider font-bold" style={{ color: "var(--text-primary)" }}>Your Order</p>
                  </div>
                  {cart.length === 0 ? (
                    <p className="text-xs py-4 text-center" style={{ color: "var(--text-tertiary)" }}>Add items from the menu</p>
                  ) : (
                    <div className="space-y-2 mb-4" data-testid="cart-items">
                      {cart.map((item) => (
                        <div key={item.label} className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate" style={{ color: "var(--text-secondary)" }}>{item.label}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <button onClick={() => updateQty(item.label, -1)} className="w-6 h-6 flex items-center justify-center" style={{ border: "1px solid var(--border)" }}><Minus size={10} /></button>
                              <span className="text-xs w-4 text-center font-semibold" style={{ fontFamily: "'JetBrains Mono'" }}>{item.qty}</span>
                              <button onClick={() => updateQty(item.label, 1)} className="w-6 h-6 flex items-center justify-center" style={{ border: "1px solid var(--border)" }}><Plus size={10} /></button>
                              <button onClick={() => removeItem(item.label)} className="w-6 h-6 flex items-center justify-center" style={{ color: "var(--error)" }}><Trash2 size={10} /></button>
                            </div>
                          </div>
                          <span className="text-sm font-semibold flex-shrink-0" style={{ color: "var(--text-primary)", fontFamily: "'JetBrains Mono', monospace" }}>${(item.price * item.qty).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between pt-3" style={{ borderTop: "1px solid var(--border)" }}>
                    <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Total</span>
                    <span className="text-lg font-bold" style={{ color: "var(--primary)", fontFamily: "'JetBrains Mono', monospace" }} data-testid="order-total">${total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-wider font-bold" style={{ color: "var(--text-primary)" }}>Contact & Shipping</p>
                  {[
                    { key: "name", label: "Full Name", obj: contact, set: setContact },
                    { key: "email", label: "Email", obj: contact, set: setContact, type: "email" },
                    { key: "phone", label: "Phone", obj: contact, set: setContact, type: "tel" },
                  ].map((f) => (
                    <div key={f.key}>
                      <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: "var(--text-tertiary)" }}>{f.label}</label>
                      <input type={f.type || "text"} value={f.obj[f.key]} data-testid={`order-${f.key}`}
                        onChange={(e) => f.set((prev) => ({ ...prev, [f.key]: e.target.value }))}
                        className="w-full px-3 py-2 text-sm outline-none" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
                    </div>
                  ))}
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: "var(--text-tertiary)" }}>Address</label>
                    <input type="text" value={shipping.line1} placeholder="Street address" data-testid="order-address1"
                      onChange={(e) => setShipping((p) => ({ ...p, line1: e.target.value }))}
                      className="w-full px-3 py-2 text-sm outline-none mb-2" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
                    <input type="text" value={shipping.line2} placeholder="Apt, suite (optional)" data-testid="order-address2"
                      onChange={(e) => setShipping((p) => ({ ...p, line2: e.target.value }))}
                      className="w-full px-3 py-2 text-sm outline-none" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[{ key: "city", label: "City" }, { key: "state", label: "State" }, { key: "zip", label: "Zip" }].map((f) => (
                      <div key={f.key}>
                        <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: "var(--text-tertiary)" }}>{f.label}</label>
                        <input type="text" value={shipping[f.key]} data-testid={`order-${f.key}`}
                          onChange={(e) => setShipping((p) => ({ ...p, [f.key]: e.target.value }))}
                          className="w-full px-3 py-2 text-sm outline-none" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
                      </div>
                    ))}
                  </div>
                  <button onClick={handleSubmit} disabled={!canSubmit || submitting} data-testid="submit-order"
                    className="w-full py-3.5 text-sm font-semibold tracking-wide flex items-center justify-center gap-2 mt-2"
                    style={{ background: canSubmit && !submitting ? "var(--primary)" : "var(--bg-tertiary)", color: canSubmit && !submitting ? "#F5F3E9" : "var(--text-tertiary)", cursor: canSubmit && !submitting ? "pointer" : "default" }}>
                    {submitting ? "Placing Order..." : "Place Order"} <ArrowRight size={16} />
                  </button>
                  <p className="text-[10px] text-center" style={{ color: "var(--text-tertiary)" }}>Redirects to CashApp to complete payment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
